/**
 * ORCA360 Part A - Session Identification Component
 * Displays session metadata in the ORCA360 CBTA format
 * Mobile: collapsible accordion (collapsed by default)
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useDevice } from '../../contexts/DeviceContext';

export interface PartAData {
  traineeName: string;
  traineeId: string;
  instructorName: string;
  instructorId: string;
  aircraftType: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  timeAsPF: string;
  timeAsPM: string;
  location: string;
  simulatorId: string;
  sessionName: string;
  overallResult: string;
  function: string;
}

interface Props {
  data: PartAData;
}

export function ORCA360PartA({ data }: Props) {
  const { isMobile } = useDevice();
  const [expanded, setExpanded] = useState(false);
  const [visibleTooltip, setVisibleTooltip] = useState<'trainee' | 'instructor' | null>(null);

  const toggleTooltip = (field: 'trainee' | 'instructor') => {
    setVisibleTooltip(prev => prev === field ? null : field);
  };

  const formatValue = (value: string | undefined | null): string => {
    if (!value || value === 'Not stated in transcript') {
      return '—';
    }
    return value;
  };

  const formatId = (value: string | undefined | null): string => {
    if (!value) return '—';
    // Show only last 8 chars of a UUID — enough to identify, not a wall of text
    return value.length > 8 ? `···${value.slice(-8)}` : value;
  };

  const getResultColor = (result: string): string => {
    switch (result?.toLowerCase()) {
      case 'pass':
        return 'text-green-700 bg-green-100';
      case 'fail':
        return 'text-red-700 bg-red-100';
      case 'incomplete':
        return 'text-yellow-700 bg-yellow-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  };

  const sessionRows = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 sm:gap-4">
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600 shrink-0">Trainee</span>
        <span className="text-sm text-navy text-right min-w-0 ml-2">{formatValue(data.traineeName)}</span>
      </div>
      <div className="border-b border-gray-100 pb-1 sm:pb-2">
        <div className="flex justify-between">
          <span className="text-sm font-medium text-gray-600 shrink-0">Trainee ID</span>
          {isMobile ? (
            <button
              type="button"
              onClick={() => toggleTooltip('trainee')}
              className="text-sm text-navy text-right min-w-0 ml-2 font-mono underline decoration-dotted"
            >
              {formatId(data.traineeId)}
            </button>
          ) : (
            <span className="text-sm text-navy text-right min-w-0 ml-2 font-mono">
              {formatValue(data.traineeId)}
            </span>
          )}
        </div>
        {isMobile && visibleTooltip === 'trainee' && (
          <div className="mt-1 px-2 py-1 bg-gray-800 text-white text-xs font-mono rounded break-all text-right">
            {data.traineeId || '—'}
          </div>
        )}
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600 shrink-0">Instructor</span>
        <span className="text-sm text-navy text-right min-w-0 ml-2">{formatValue(data.instructorName)}</span>
      </div>
      <div className="border-b border-gray-100 pb-1 sm:pb-2">
        <div className="flex justify-between">
          <span className="text-sm font-medium text-gray-600 shrink-0">Instructor ID</span>
          {isMobile ? (
            <button
              type="button"
              onClick={() => toggleTooltip('instructor')}
              className="text-sm text-navy text-right min-w-0 ml-2 font-mono underline decoration-dotted"
            >
              {formatId(data.instructorId)}
            </button>
          ) : (
            <span className="text-sm text-navy text-right min-w-0 ml-2 font-mono">
              {formatValue(data.instructorId)}
            </span>
          )}
        </div>
        {isMobile && visibleTooltip === 'instructor' && (
          <div className="mt-1 px-2 py-1 bg-gray-800 text-white text-xs font-mono rounded break-all text-right">
            {data.instructorId || '—'}
          </div>
        )}
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600 shrink-0">Aircraft Type/Variant</span>
        <span className="text-sm text-navy text-right min-w-0 ml-2">{formatValue(data.aircraftType)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600 shrink-0">Session Date</span>
        <span className="text-sm text-navy text-right min-w-0 ml-2">{formatValue(data.sessionDate)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600 shrink-0">Start (hh:mm) LT</span>
        <span className="text-sm text-navy text-right min-w-0 ml-2">{formatValue(data.startTime)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600 shrink-0">End (hh:mm) LT</span>
        <span className="text-sm text-navy text-right min-w-0 ml-2">{formatValue(data.endTime)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600 shrink-0">Time as PF (hh:mm)</span>
        <span className="text-sm text-navy font-medium text-right min-w-0 ml-2">{formatValue(data.timeAsPF)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600 shrink-0">Time as PM (hh:mm)</span>
        <span className="text-sm text-navy font-medium text-right min-w-0 ml-2">{formatValue(data.timeAsPM)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600 shrink-0">Location</span>
        <span className="text-sm text-navy text-right min-w-0 ml-2">{formatValue(data.location)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600 shrink-0">Simulator ID</span>
        <span className="text-sm text-navy text-right min-w-0 ml-2">{formatValue(data.simulatorId)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600 shrink-0">Session</span>
        <span className="text-sm text-navy text-right min-w-0 ml-2">{formatValue(data.sessionName)}</span>
      </div>
      <div className="flex justify-between border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600 shrink-0">Overall Result</span>
        <span className={`text-sm px-2 py-0.5 rounded font-medium ${getResultColor(data.overallResult)}`}>
          {formatValue(data.overallResult)}
        </span>
      </div>
      <div className="flex justify-between md:col-span-2 border-b border-gray-100 pb-1 sm:pb-2">
        <span className="text-sm font-medium text-gray-600 shrink-0">Function</span>
        <span className="text-sm text-navy text-right min-w-0 ml-2">{formatValue(data.function)}</span>
      </div>
    </div>
  );

  /* ── Mobile: collapsible accordion (collapsed by default) ── */
  if (isMobile) {
    return (
      <div className="border-y border-slate-200 bg-white overflow-hidden">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center gap-2 px-2 py-2 text-left touch-target"
        >
          {expanded ? (
            <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
          ) : (
            <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />
          )}
          <span className="text-sm font-bold text-navy">Part A — Session Identification</span>
          {!expanded && (
            <span className="ml-auto text-[10px] text-slate-400 truncate max-w-[40%]">
              {formatValue(data.traineeName)} • {formatValue(data.sessionDate)}
            </span>
          )}
        </button>
        {expanded && (
          <div className="px-2 pb-2 border-t border-slate-100 pt-2">
            {sessionRows}
          </div>
        )}
      </div>
    );
  }

  /* ── Desktop: always open card ── */
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-lg font-bold text-navy mb-4 border-b border-gray-200 pb-2">
        Part A — Session Identification
      </h2>
      {sessionRows}
    </div>
  );
}

export default ORCA360PartA;

